import { useState, useEffect, useRef, useCallback } from 'react';
import { coursesApi } from '../services/api';

/**
 * Hook for polling student environment status
 * Automatically updates status and cleans up on unmount
 *
 * @param {number} assignmentId - Assignment ID
 * @param {string} token - Auth token
 * @param {number} pollInterval - Poll interval in ms (default: 3000)
 * @param {boolean} enabled - Whether polling is enabled (default: true)
 */
export const useEnvironmentStatus = (assignmentId, token, pollInterval = 3000, enabled = true) => {
  const [environment, setEnvironment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const intervalRef = useRef(null);
  const mountedRef = useRef(true);

  const fetchStatus = useCallback(async () => {
    if (!assignmentId || !token || !mountedRef.current) return;

    try {
      const status = await coursesApi.getAssignmentStatus(assignmentId, token);

      if (mountedRef.current) {
        setEnvironment(status.sandbox_environment || null);
        setLoading(false);
        setError(null);
      }
    } catch (err) {
      if (mountedRef.current) {
        console.error('Error fetching environment status:', err);
        setError(err.message);
        setLoading(false);
      }
    }
  }, [assignmentId, token]);

  // Initial fetch
  useEffect(() => {
    if (enabled) {
      fetchStatus();
    }
  }, [fetchStatus, enabled]);

  // Setup polling
  useEffect(() => {
    if (!enabled || !assignmentId || !token) return;

    // Only poll if environment is in creating status
    if (environment?.status === 'creating') {
      intervalRef.current = setInterval(fetchStatus, pollInterval);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [enabled, assignmentId, token, environment?.status, pollInterval, fetchStatus]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      mountedRef.current = false;
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  const refresh = useCallback(() => {
    setLoading(true);
    fetchStatus();
  }, [fetchStatus]);

  return {
    environment,
    loading,
    error,
    refresh,
    isCreating: environment?.status === 'creating',
    isActive: environment?.status === 'active',
    isDestroyed: environment?.status === 'destroyed' || environment?.status === 'archived',
    isFailed: environment?.status === 'error',
  };
};
